import { IRWCAnalyticsLibrary, UserTraits } from '@ringcentral/rwc-utils/src/analytics/types';
import { getMixpanelMaskedProps } from './analytics-masked-props';
import { baseProps, getAnalyticsInstance, logger } from './analytics-provider';

/**
 * Wraps key methods of analytics instance and prevents sensitive data from leaking out
 * by overriding several automatically collected internal properties when needed.
 */
export const secureAnalytics: IRWCAnalyticsLibrary = {
    /**
     * Used to identify user with some permanent props of his account or session
     */
    identify(userId: string | null, userTraits: UserTraits): void {
        const analyticsInstance = getAnalyticsInstance();
        if (userId) {
            logger.debug('identify', userId, userTraits);
        }
        try {
            analyticsInstance?.identify(userId, userTraits);
        } catch (error) {
            logger.error('Failed to identify user', error);
        }
    },
    setUserProfile(userTraits: UserTraits): void {
        logger.debug('setUserProfile', userTraits);
        try {
            getAnalyticsInstance()?.setUserProfile(userTraits);
        } catch (error) {
            logger.error('Failed to set user profile', error);
        }
    },
    removeTraits(traits: Array<string>): void {
        logger.debug('removeTraits', traits);
        try {
            getAnalyticsInstance()?.removeTraits(traits);
        } catch (error) {
            logger.error('Failed to remove traits', error);
        }
    },
    getProperty(propertyName: string): unknown {
        try {
            return getAnalyticsInstance()?.getProperty(propertyName);
        } catch (error) {
            logger.error('Failed to get property', error);
            return undefined;
        }
    },
    page(pageName: string, props?: UserTraits): void {
        logger.debug('page', pageName);

        try {
            getAnalyticsInstance()?.page(pageName, { ...baseProps, ...props });
        } catch (error) {
            logger.error('Failed to track page', error);
        }
    },
    /**
     * Note: prefer `trackEvent` utility to `secureAnalytics.track` when possible,
     * as the `trackEvent` augments each event with extra info, which is extremely useful when analyzing data.
     */
    track(event: string, props: UserTraits = {}): void {
        const maskedProps = { ...baseProps, ...props, ...getMixpanelMaskedProps() };
        logger.debug('track', event, maskedProps);
        try {
            getAnalyticsInstance()?.track(event, maskedProps);
        } catch (error) {
            logger.error('Failed to track event', error);
        }
    },
    alias(newId: string, previousId: string) {
        try {
            getAnalyticsInstance()?.alias(newId, previousId);
        } catch (error) {
            logger.error('Failed to alias user', error);
        }
    },
    group(company: string, companyId: string, traits?: UserTraits) {
        try {
            getAnalyticsInstance()?.group(company, companyId, traits);
        } catch (error) {
            logger.error('Failed to group', error);
        }
    },
    reset() {
        try {
            getAnalyticsInstance()?.reset();
        } catch (error) {
            logger.error('Failed to reset', error);
        }
    },
    setConfig(config: Record<string, unknown>) {
        try {
            getAnalyticsInstance()?.setConfig(config);
        } catch (error) {
            logger.error('Failed to setConfig', error);
        }
    },
    flush() {
        const analyticsInstance = getAnalyticsInstance() as IRWCAnalyticsLibrary | null;

        try {
            analyticsInstance?.flush();
        } catch (error) {
            logger.error('Failed to flush', error);
        }
    },
    startFlushingBatches(): void {
        const analyticsInstance = getAnalyticsInstance() as IRWCAnalyticsLibrary | null;

        try {
            analyticsInstance?.startFlushingBatches();
        } catch (error) {
            logger.error('Failed to start flushing batches', error);
        }
    },
    stopFlushingBatches(): void {
        const analyticsInstance = getAnalyticsInstance() as IRWCAnalyticsLibrary | null;

        try {
            analyticsInstance?.stopFlushingBatches();
        } catch (error) {
            logger.error('Failed to stop flushing batches', error);
        }
    },
    isBatchingStopped(): boolean {
        const analyticsInstance = getAnalyticsInstance() as IRWCAnalyticsLibrary | null;

        try {
            return analyticsInstance?.isBatchingStopped() ?? false;
        } catch (error) {
            logger.error('Failed to check batching status', error);
            return false;
        }
    },
    updateQueuedProperties(updtFn: (tr: UserTraits) => UserTraits): boolean {
        const analyticsInstance = getAnalyticsInstance() as IRWCAnalyticsLibrary | null;

        try {
            return analyticsInstance?.updateQueuedProperties(updtFn) ?? false;
        } catch (error) {
            logger.error('Failed to update queued properties', error);
            return false;
        }
    },
};
